import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PackageSearch, Download, Loader2 } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiFeedback } from '../../../lib/useApiFeedback';
import { fmtINR } from '../../../components/admin/billing/shared';
import { DateRange, ReportHeader, StatCard, today } from './_shared';

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const SLOW = [30, 60, 90, 180];

const SalesTable = ({ parts, sortBy, onSort }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="ps-sales-table">
    <div className="flex items-center justify-between px-4 py-3">
      <h2 className="text-sm font-semibold text-slate-900">Top sellers</h2>
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
        {[['revenue', 'By revenue'], ['qty', 'By quantity'], ['margin', 'By margin']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => onSort(k)} className={`rounded-md px-3 py-1 text-xs font-semibold ${sortBy === k ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`ps-sort-${k}`}>{l}</button>
        ))}
      </div>
    </div>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="px-4 py-2">Part</th><th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2 text-right">Revenue (ex GST)</th><th className="px-4 py-2 text-right">Cost</th><th className="px-4 py-2 text-right">Margin</th><th className="px-4 py-2 text-right">Bills</th></tr>
      </thead>
      <tbody>
        {parts.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-testid="ps-sales-empty">No parts sold in this period.</td></tr>}
        {parts.map((p) => (
          <tr key={p.id} className="border-t border-slate-100" data-testid={`ps-part-${p.id}`}>
            <td className="px-4 py-2.5"><div className="font-medium text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{[p.sku, p.brand].filter(Boolean).join(' · ')}</div></td>
            <td className="px-4 py-2.5 text-right tabular-nums">{p.qty} {p.unit}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">₹ {fmtINR(p.revenue)}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{p.has_cost ? `₹ ${fmtINR(p.cost)}` : <span className="text-amber-600" title="Add a purchase cost to this part in Inventory">No cost</span>}</td>
            <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${p.margin < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{p.has_cost ? <>₹ {fmtINR(p.margin)} <span className="text-xs font-normal text-slate-500">({p.margin_pct}%)</span></> : '—'}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{p.bills}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SlowTable = ({ rows, days, onDays }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="ps-slow-table">
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
      <div><h2 className="text-sm font-semibold text-slate-900">Slow movers</h2><p className="text-[11px] text-slate-500">Parts for sale, in stock, with no sale in the last {days} days.</p></div>
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
        {SLOW.map((d) => <button key={d} type="button" onClick={() => onDays(d)} className={`rounded-md px-3 py-1 text-xs font-semibold ${days === d ? 'btn-ink' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`ps-slow-${d}`}>{d} days</button>)}
      </div>
    </div>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="px-4 py-2">Part</th><th className="px-4 py-2 text-right">In stock</th><th className="px-4 py-2">Last sold</th><th className="px-4 py-2 text-right">Money tied up</th></tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500" data-testid="ps-slow-empty">Nothing is gathering dust. Every part for sale has moved recently.</td></tr>}
        {rows.map((p) => (
          <tr key={p.id} className="border-t border-slate-100" data-testid={`ps-slow-row-${p.id}`}>
            <td className="px-4 py-2.5"><div className="font-medium text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{[p.sku, p.brand].filter(Boolean).join(' · ')}</div></td>
            <td className="px-4 py-2.5 text-right tabular-nums">{p.quantity} {p.unit}</td>
            <td className="px-4 py-2.5 text-slate-600">{p.last_sold || 'Never'}{p.days_idle != null && <span className="text-xs text-slate-400"> · {p.days_idle} days idle</span>}</td>
            <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-amber-700">₹ {fmtINR(p.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PartsSalesReport() {
  const { authHeader } = useAuth();
  const { showError } = useApiFeedback();
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [days, setDays] = useState(90);
  const [sortBy, setSortBy] = useState('revenue');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = { date_from: from, date_to: to, slow_days: days };
  const load = useCallback(() => {
    axios.get(`${API}/reports/parts-sales`, { params: { date_from: from, date_to: to, slow_days: days }, headers: authHeader() })
      .then((r) => setData(r.data)).catch(showError);
  }, [from, to, days, authHeader, showError]);
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/reports/parts-sales`, { params: { ...params, format: 'xlsx' }, headers: authHeader(), responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data); a.download = `parts-sales-${from}-to-${to}.xlsx`; a.click();
    } catch (e) { showError(e); } finally { setBusy(false); }
  };

  const t = data?.totals;
  const parts = [...(data?.parts || [])].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  return (
    <div className="min-h-full space-y-4 bg-slate-50 p-4 sm:p-6" data-testid="parts-sales-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ReportHeader icon={PackageSearch} title="Parts Sales" />
        <button type="button" onClick={exportXlsx} disabled={busy || !data} className="inline-flex items-center gap-1.5 rounded-lg btn-ink px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="ps-export-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel
        </button>
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={load} />
      {t && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Parts revenue (ex GST)" value={`₹ ${fmtINR(t.revenue)}`} hint={`${t.qty} units sold`} testid="ps-stat-revenue" />
          <StatCard label="Margin" value={`₹ ${fmtINR(t.margin)}`} hint={t.margin_pct != null ? `${t.margin_pct}% on costed parts` : 'Add purchase costs to see margin'} color="text-emerald-700" testid="ps-stat-margin" />
          <StatCard label="Parts without a cost" value={t.uncosted} hint="Left out of margin" color={t.uncosted ? 'text-amber-600' : 'text-slate-900'} testid="ps-stat-uncosted" />
          <StatCard label={`Tied up in slow stock (${days}d)`} value={`₹ ${fmtINR(t.slow_value)}`} hint={`${t.slow_count} parts`} color="text-amber-700" testid="ps-stat-slow" />
        </div>
      )}
      <SalesTable parts={parts} sortBy={sortBy} onSort={setSortBy} />
      <SlowTable rows={data?.slow_movers || []} days={days} onDays={setDays} />
    </div>
  );
}
